import classNames from 'classnames';
import { Circle, GoogleAPI, GoogleApiWrapper, IMarkerProps, Map } from 'google-maps-react';
import { Pathname, Search } from 'history';
import { isEmpty } from 'lodash';
import { Component } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { connect } from 'react-redux';

import { ReactComponent as IconCheck } from '../../../../assets/images/check-white.svg';
import { ReactComponent as IconClose } from '../../../../assets/images/close.svg';

import { CoordinateMode, CoordinateModel } from '../../../../models/coordinate.model';
import { ProductModel } from '../../../../models/products.model';
import { removeProductFromCollection, updateCollection } from '../../../../store/collection';
import { AppState } from '../../../../store/store';
import { PRODUCT_PATH } from '../../Navigator';
import Button, { ButtonKind, ButtonSize } from '../button/Button';
import Divider from '../Divider';
import ExchangeDuration from '../ExchangeDuration';
import { InfoWindow, Marker } from '../gmap';
import Id from '../id';
import Price from '../Price';
import styles from '../product/product.module.scss';
import StrongText from '../StrongText';

import './index.scss';

const PLACES_API_KEY = process.env.REACT_APP_PLACES_API_KEY;

export interface IMarker extends google.maps.Marker {
    name: string;
}

export interface Position {
    lat: number;
    lng: number;
}

interface State {
    activeMarker?: google.maps.Marker | null;
    showingInfoWindow?: string | null;
}

interface Props {
    center?: google.maps.LatLngLiteral;
    coordinateLocale?: CoordinateModel;
    getSearch: (s: string) => string;
    google: GoogleAPI;
    initialCenter?: google.maps.LatLngLiteral;
    intl: IntlShape;
    locations: ProductModel[];
    push?: (location: { pathname?: Pathname; search?: Search }) => void;
    useCustomMarker?: boolean;
    zoom?: number;
}

interface StateProps {
    collection?: string[];
}

interface DispatchProps {
    addToCollection: (urn: string) => void;
    removeFromCollection: (urn: string) => void;
}

class MapContainer extends Component<Props & DispatchProps & StateProps, State> {
    state = {
        showingInfoWindow: null,
        activeMarker: null,
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    handleViewClicks = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { push, getSearch } = this.props;
        const { showingInfoWindow } = this.state;
        const urn = String(showingInfoWindow);

        if (push && urn) {
            push({ pathname: PRODUCT_PATH, search: getSearch(urn) });
        }
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    handleAddClicks = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { push, addToCollection, removeFromCollection } = this.props;
        const { showingInfoWindow } = this.state;
        const urn = String(showingInfoWindow);

        if (push && urn) {
            this.isActiveLocation(urn) ? removeFromCollection(urn) : addToCollection(urn);
        }
    };

    infoWindowOpened = () => {
        const productAddEvent = document.querySelector('[id^="productAdd"]');
        if (productAddEvent) {
            productAddEvent.addEventListener('click', this.handleAddClicks);
        }

        const productViewEvent = document.querySelector('[id^="productView"]');
        if (productViewEvent) {
            productViewEvent.addEventListener('click', this.handleViewClicks);
        }
    };

    isActiveLocation = (urn: string) => this.props.collection?.join('---').includes(urn);

    isShowingInfoBlock = (name: string) => this.state.activeMarker && this.state.showingInfoWindow === name;

    onInfoWindowClose = () => {
        this.setState({ showingInfoWindow: null, activeMarker: null });
        const productAddEvent = document.querySelector('[id^="productAdd"]');
        if (productAddEvent) {
            productAddEvent.removeEventListener('click', this.handleAddClicks);
        }

        const productViewEvent = document.querySelector('[id^="productView"]');
        if (productViewEvent) {
            productViewEvent.removeEventListener('click', this.handleViewClicks);
        }
    };

    onMapClicked = () => {
        if (this.state.showingInfoWindow) {
            this.onInfoWindowClose();
        }
    };

    onMarkerClick = (_props?: IMarkerProps, marker?: IMarker) =>
        this.setState({ activeMarker: marker, showingInfoWindow: marker?.name });

    renderMarker = (location: ProductModel, idx: number, useCustomMarker: boolean, position?: Position) => {
        const { latitude, longitude, producturn, format, address } = location;

        return (
            <Marker
                onClick={this.onMarkerClick}
                key={`marker-${format}-${producturn}-${position ? position.lng : longitude}`}
                idx={idx}
                title={address}
                name={producturn || address}
                useCustomMarker={useCustomMarker}
                position={position ? position : { lat: latitude, lng: longitude }}
            />
        );
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    render() {
        const { google, zoom, center, locations, useCustomMarker, intl, coordinateLocale } = this.props;
        const radius = +(coordinateLocale?.radius ?? 0);
        const { activeMarker, showingInfoWindow } = this.state;
        const lngLtdRadius: Position = {
            lat: +(coordinateLocale?.latitude ?? 0),
            lng: +(coordinateLocale?.longitude ?? 0),
        };
        const lngLtdRadiusDefault: Position = {
            lat: 47.37699413225787,
            lng: 8.548941674258733,
        };
        const locationCoords =
            !coordinateLocale?.name && +(coordinateLocale?.mode ?? 0) === CoordinateMode.Radius ? lngLtdRadius : null;

        return (
            <Map
                google={google}
                onClick={this.onMapClicked}
                zoom={zoom ? zoom : 8}
                center={lngLtdRadius.lng ? lngLtdRadius : center || lngLtdRadiusDefault}
                initialCenter={lngLtdRadius.lat ? lngLtdRadius : center || lngLtdRadiusDefault}
                containerStyle={{ position: 'relative' }}
            >
                {!isEmpty(locations) &&
                    locations.map((location, idx: number) => {
                        const {
                            productphotopath,
                            producturn,
                            format,
                            priceinformation,
                            address,
                            city,
                            mediainfo,
                            pricedays,
                        } = location;

                        return [
                            this.renderMarker(location, idx, !!useCustomMarker),
                            this.isShowingInfoBlock(producturn) && (
                                <InfoWindow
                                    marker={activeMarker}
                                    onOpen={this.infoWindowOpened}
                                    onClose={this.onInfoWindowClose}
                                    key={`info-${idx}`}
                                    id={`info-${idx}`}
                                    ref={`info-${idx}`}
                                    google={google}
                                    map={google.maps}
                                    visible={showingInfoWindow === producturn}
                                >
                                    <div className={'info-container__image-wrapper'}>
                                        <div className={styles.imgWrapperInfo}>
                                            <img src={`${productphotopath}&resolution=thumbnail`} alt="Product image" />
                                        </div>

                                        <div className={'info-container__image-wrapper--title'}>
                                            <StrongText className={styles.formatInfo}>{format}</StrongText>
                                            <Id id={String(mediainfo.nummer)} intl={intl} />
                                        </div>
                                    </div>

                                    <div className={styles.descriptionInfo}>
                                        <Divider />

                                        <div className={styles.address}>
                                            {address} {city}
                                        </div>

                                        <ExchangeDuration duration={priceinformation.pricedays} intl={intl} />

                                        <Price
                                            className={styles.price}
                                            priceinformation={priceinformation}
                                            withGross
                                            duration={pricedays}
                                            withEmptyText
                                            intl={intl}
                                        />
                                    </div>

                                    <div id="infoButtonsWrapper">
                                        <Button
                                            className={classNames('mb-10 mt-40', {
                                                isActive: this.isActiveLocation(producturn),
                                            })}
                                            size={ButtonSize.FULL}
                                            id={`productAdd${producturn}`}
                                        >
                                            <div className="buttonContainer">
                                                <div className="remove">
                                                    <IconClose width="10" height="10" stroke="#fff" strokeWidth="4" />
                                                </div>
                                                <div className="adding">
                                                    <div>{this.isActiveLocation(producturn) ? <IconCheck /> : '+'}</div>
                                                    <div>
                                                        {this.isActiveLocation(producturn)
                                                            ? intl.formatMessage({
                                                                  id: 'map.removeFromCollection',
                                                                  defaultMessage: 'Zur Liste hinzugefügt',
                                                              })
                                                            : intl.formatMessage({
                                                                  id: 'map.addToCollection',
                                                                  defaultMessage: 'Merkliste',
                                                              })}
                                                    </div>
                                                </div>
                                            </div>
                                        </Button>

                                        <Button
                                            kind={ButtonKind.INVERT}
                                            size={ButtonSize.FULL}
                                            id={`productView${producturn}`}
                                        >
                                            {intl.formatMessage({
                                                id: 'map.viewDetails',
                                                defaultMessage: 'Details anzeigen',
                                            })}
                                        </Button>
                                    </div>
                                </InfoWindow>
                            ),
                        ];
                    })}

                {radius &&
                    lngLtdRadius.lat && [
                        <Circle
                            key={'circle-location'}
                            radius={radius}
                            center={lngLtdRadius}
                            strokeColor="transparent"
                            strokeOpacity={0}
                            strokeWeight={5}
                            fillColor="#FF0000"
                            fillOpacity={0.2}
                        />,
                        !locationCoords && (
                            <Marker
                                key={`marker-${lngLtdRadius.lng}-${radius}`}
                                name={'custom-location-marker'}
                                position={lngLtdRadius}
                            />
                        ),
                    ]}

                {locationCoords && (
                    <Marker
                        key={`custom-marker-${locationCoords.lng}`}
                        title={intl.formatMessage({ id: 'map.myLocation', defaultMessage: 'Mein Standort' })}
                        name={'custom-location-marker'}
                        useLocationMarker
                        position={locationCoords}
                    />
                )}
            </Map>
        );
    }
}

const mapState = (state: AppState) => ({
    collection: state.collection.collection,
    coordinateLocale: state.location.coordinate,
});

const mapDispatch = {
    addToCollection: (urn: string) => updateCollection(urn),
    removeFromCollection: (urn: string) => removeProductFromCollection(urn),
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const connector = connect<StateProps, DispatchProps>(mapState, mapDispatch)(MapContainer);

export default GoogleApiWrapper({ apiKey: PLACES_API_KEY as string })(injectIntl(connector));
